<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import ExpertBillingCheckoutCard from "@/app/components/settings/ExpertBillingCheckoutCard.vue";
import ExpertPasswordChangeCard from "@/app/components/settings/ExpertPasswordChangeCard.vue";
import ExpertPersonalDetailsCard from "@/app/components/settings/ExpertPersonalDetailsCard.vue";
import SettingsPageLayout from "@/app/components/settings/SettingsPageLayout.vue";
import SubscriptionSummaryCard from "@/app/components/settings/SubscriptionSummaryCard.vue";
import SettingsInfo from "@/components/common/cards/SettingsInfo.vue";
import { useExpertProfile } from "@/app/composables/useExpertProfile";
import {
    captureExpertCheckoutLocation,
    consumeHeldBillingInvitationToken,
    type BillingCheckoutReturnState,
} from "@/app/composables/expert-billing/expertCheckout";
import { useExpertSubscriptionOverview } from "@/app/composables/expert-billing/useExpertSubscriptionOverview";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/date";

const authStore = useAuthStore();
const route = useRoute();

const invitationToken = ref<string | null>(null);
const checkoutReturnState = ref<BillingCheckoutReturnState>({ kind: "none" });
const checkoutCardKey = ref(0);
let heldInvitationToken = consumeHeldBillingInvitationToken();

// Capture before the checkout card can request anything, then immediately
// remove secrets and return parameters from the address bar. A same-document
// invitation navigation reuses this page component, so route/hash changes must
// be captured as well as the initial mount.
function captureCheckoutSignals() {
    const captured = captureExpertCheckoutLocation(window.location);
    if (captured.changed) {
        window.history.replaceState(window.history.state, "", captured.cleanRelativeUrl);
    }

    const nextInvitationToken = captured.invitationToken ?? heldInvitationToken;
    heldInvitationToken = null;
    const hasCheckoutSignal = Boolean(nextInvitationToken) || captured.returnState.kind !== "none";
    if (!hasCheckoutSignal) return;

    invitationToken.value = nextInvitationToken;
    checkoutReturnState.value = captured.returnState;
    checkoutCardKey.value += 1;
}

watch(() => route.fullPath, captureCheckoutSignals, { immediate: true, flush: "sync" });
window.addEventListener("hashchange", captureCheckoutSignals);
onBeforeUnmount(() => window.removeEventListener("hashchange", captureCheckoutSignals));

const isExpertRole = computed(() => authStore.user?.role_id === 3);
const { profile, userType, isLoading, error, refetch } = useExpertProfile();
const confirmingPayment = computed(() => checkoutReturnState.value.kind === "success");
const {
    overview,
    isLoading: isBillingLoading,
    error: billingError,
    refetch: refetchBilling,
    isPaymentConfirmed,
    confirmationTimedOut,
    openBillingPortal,
    isOpeningPortal,
    portalError,
} = useExpertSubscriptionOverview({
    enabled: isExpertRole,
    confirmingPayment,
});

const subscriptionDetails = computed(() => {
    const memberSince = authStore.user?.created_at ? formatDate(authStore.user.created_at) : "Not available";

    return {
        accountType: userType.value === "agency" ? "Agency" : "Freelancer",
        memberSince,
    };
});
</script>

<template>
    <SettingsPageLayout
        title="Settings"
        :is-loading="isLoading"
        :error="error"
        :on-retry="async () => { await refetch(); }"
    >
        <template #persistent>
            <ExpertBillingCheckoutCard
                :key="checkoutCardKey"
                class="mb-8 max-w-[1184px]"
                :enabled="isExpertRole"
                :is-expert-role="isExpertRole"
                :invitation-token="invitationToken"
                :return-state="checkoutReturnState"
                :payment-confirmed="isPaymentConfirmed"
                :confirmation-timed-out="confirmationTimedOut"
            />
        </template>

        <div
            v-if="profile"
            class="grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_1fr] gap-x-16 gap-y-16 max-w-[1184px]"
        >
            <SubscriptionSummaryCard
                class="lg:col-span-2"
                :account-type="subscriptionDetails.accountType"
                :member-since="subscriptionDetails.memberSince"
                :overview="overview"
                :is-loading="isBillingLoading"
                :error="billingError"
                :is-opening-portal="isOpeningPortal"
                :portal-error="portalError"
                @manage="openBillingPortal"
                @retry="refetchBilling"
            />

            <div class="flex flex-col gap-8 lg:contents">
                <SettingsInfo
                    title="Email Address"
                    description="Update the email address you use to sign in and receive account notifications."
                />
                <ExpertPersonalDetailsCard />
            </div>

            <div class="flex flex-col gap-8 lg:contents">
                <SettingsInfo
                    title="Change Password"
                    description="Update your password to keep your account secure."
                />
                <ExpertPasswordChangeCard />
            </div>
        </div>
    </SettingsPageLayout>
</template>
